import { hasAnyPermission } from './permissions.js';
export { resolvePermissionsForRoles } from './role-permissions.js';

export interface MockAuthProfile {
  readonly code: string;
  readonly displayName: string;
  readonly subjectId: string;
  readonly username: string;
  readonly email: string;
  readonly organizationId?: string;
  readonly roles: readonly string[];
  readonly permissions: readonly string[];
  readonly tokenAliases?: readonly string[];
}

export interface MockAuthorizationSubject {
  readonly subjectId: string;
  readonly username: string;
  readonly displayName: string;
  readonly email: string;
  readonly organizationId?: string;
  readonly roles: readonly string[];
  readonly permissions: readonly string[];
  readonly profileCode: string;
}



const REFERENTIAL_ADMIN_PERMISSIONS = [
  'REFERENTIAL_DOMAIN_VIEW',
  'REFERENTIAL_DOMAIN_CREATE',
  'REFERENTIAL_DOMAIN_UPDATE',
  'REFERENTIAL_DOMAIN_DELETE',
  'REFERENTIAL_CATEGORY_VIEW',
  'REFERENTIAL_CATEGORY_CREATE',
  'REFERENTIAL_CATEGORY_UPDATE',
  'REFERENTIAL_CATEGORY_DELETE',
  'REFERENTIAL_ITEM_VIEW',
  'REFERENTIAL_ITEM_CREATE',
  'REFERENTIAL_ITEM_UPDATE',
  'REFERENTIAL_ITEM_DELETE',
  'REFERENTIAL_ITEM_ACTIVATE',
  'REFERENTIAL_ITEM_DEACTIVATE',
  'REFERENTIAL_IMPORT_PREVIEW',
  'REFERENTIAL_IMPORT_EXECUTE',
  'REFERENTIAL_EXPORT',
  'REFERENTIAL_USAGE_VIEW',
  'REFERENTIAL_USAGE_REGISTER',
  'REFERENTIAL_LOOKUP_READ',
  'referential.admin.manage',
] as const;

const GESTION_ACCESS_ADMIN_PERMISSIONS = [
  'GESTION_ACCESS_VIEW',
  'GESTION_ACCESS_CREATE',
  'GESTION_ACCESS_UPDATE_OWN',
  'GESTION_ACCESS_VALIDATE_ZONE',
  'GESTION_ACCESS_VALIDATE_SECURITY',
  'GESTION_ACCESS_VALIDATE_PAYMENT',
  'GESTION_ACCESS_VALIDATE_DGSN',
  'GESTION_ACCESS_ADMIN',
  'GESTION_ACCESS_AUDIT_VIEW',
  'GESTION_ACCESS_DOCUMENT_UPLOAD',
] as const;

const PLATFORM_ADMIN_PERMISSIONS = [
  'OWNER_ACCESS',
  'DASHBOARD_VIEW',
  'IDENTITIES_VIEW',
  'IDENTITIES_MANAGE',
  'ORGANIZATIONS_VIEW',
  'ORGANIZATIONS_MANAGE',
  'ORGANIZATIONS_UPDATE',
  'ORGANIZATIONS_TRANSFER_OWNERSHIP',
  'AUTHORIZATION_VIEW',
  'AUTHORIZATION_MANAGE',
  'AUDIT_VIEW',
  ...REFERENTIAL_ADMIN_PERMISSIONS,
  ...GESTION_ACCESS_ADMIN_PERMISSIONS,
  'dum.view',
  'dum.manage',
  'dum.admin',
  'flow.access',
  'flow.operations.*',
  'flow.analytics.*',
  'integration.execution.*',
  'integration.dlq.*',
  'integration.replay.*',
  'integration.partner.view',
  'integration.partner.create',
  'integration.partner.edit',
  'integration.partner.delete',
  'integration.partner.submit',
  'integration.partner.approve',
  'integration.partner.reject',
  'integration.partner.suspend',
  'integration.partner.reactivate',
  'integration.partner.rollback',
  'integration.partner.connectivity.test',
  'integration.partner.audit.view',
  'integration.secret.create',
  'integration.secret.validate',
  'integration.secret.metadata.view',
  'integration.secret.rotate',
  'ens.filing.create',
  'ens.filing.read.all',
  'ens.filing.update.draft',
  'ens.filing.delete.draft',
  'ens.filing.submit',
  'ens.filing.copy',
  'ens.filing.amend',
  'ens.filing.print',
  'ens.filing.export',
  'ens.exchange.read',
  'ens.exchange.read.raw',
  'ens.exchange.replay',
  'ens.reconciliation.read',
  'ens.reconciliation.manage',
  'ens.reconciliation.correlate',
  'ens.profile.read',
  'ens.profile.manage',
  'ens.audit.read',
] as const;

const PLATFORM_OPERATOR_PERMISSIONS = [
  'DASHBOARD_VIEW',
  'IDENTITIES_VIEW',
  'IDENTITIES_MANAGE',
  'ORGANIZATIONS_VIEW',
  'ORGANIZATIONS_MANAGE',
  'AUTHORIZATION_VIEW',
  'AUTHORIZATION_MANAGE',
] as const;

const PLATFORM_AUDITOR_PERMISSIONS = [
  'DASHBOARD_VIEW',
  'AUDIT_VIEW',
] as const;

const FLOW_ADMIN_PERMISSIONS = [
  'flow.access',
  'flow.operations.*',
  'flow.analytics.*',
  'integration.execution.*',
  'integration.dlq.*',
  'integration.replay.*',
  'SCANNER_MONITOR_VIEW',
  'SCANNER_MONITOR_VIEW_XML',
  'SCANNER_MONITOR_VIEW_IMAGES',
  'SCANNER_MONITOR_VIEW_ERRORS',
  'SCANNER_MONITOR_RETRY',
  'SCANNER_MONITOR_EXPORT',
] as const;


const MOCK_AUTH_PROFILES: readonly MockAuthProfile[] = [
  {
    code: 'platform-admin',
    displayName: 'Platform Admin',
    subjectId: 'mock-platform-admin',
    username: 'platform-admin',
    email: 'platform-admin@oss-nwm.local',
    organizationId: 'org-1',
    roles: ['platform-admin', 'platform-operator', 'auditor', 'ACCESS_ADMIN'],
    permissions: PLATFORM_ADMIN_PERMISSIONS,
  },
  {
    code: 'platform-operator',
    displayName: 'Platform Operator',
    subjectId: 'mock-platform-operator',
    username: 'platform-operator',
    email: 'platform-operator@oss-nwm.local',
    roles: ['platform-operator'],
    permissions: PLATFORM_OPERATOR_PERMISSIONS,
  },
  {
    code: 'platform-auditor',
    displayName: 'Platform Auditor',
    subjectId: 'mock-platform-auditor',
    username: 'platform-auditor',
    email: 'platform-auditor@oss-nwm.local',
    roles: ['auditor'],
    permissions: PLATFORM_AUDITOR_PERMISSIONS,
  },
  {
    code: 'flow-admin',
    displayName: 'FLOW Admin',
    subjectId: 'mock-flow-admin',
    username: 'flow-admin',
    email: 'flow-admin@oss-nwm.local',
    roles: ['flow-admin', 'oss.flow.admin'],
    permissions: FLOW_ADMIN_PERMISSIONS,
    tokenAliases: ['flow-admin-token', 'admin-token'],
  },
  {
    code: 'flow-viewer',
    displayName: 'FLOW Viewer',
    subjectId: 'mock-flow-viewer',
    username: 'flow-viewer',
    email: 'flow-viewer@oss-nwm.local',
    roles: ['flow-viewer'],
    permissions: ['flow.access'],
    tokenAliases: ['viewer-token'],
  },
  {
    code: 'flow-operator',
    displayName: 'FLOW Operator',
    subjectId: 'mock-flow-operator',
    username: 'flow-operator',
    email: 'flow-operator@oss-nwm.local',
    roles: ['flow-operator'],
    permissions: [
      'flow.access',
      'flow.operations.execute',
      'integration.execution.retry',
      'integration.execution.replay',
      'integration.dlq.requeue',
      'integration.dlq.discard',
    ],
    tokenAliases: ['operator-token'],
  },
  {
    code: 'flow-configurator',
    displayName: 'FLOW Configurator',
    subjectId: 'mock-flow-configurator',
    username: 'flow-configurator',
    email: 'flow-configurator@oss-nwm.local',
    roles: ['flow-configurator'],
    permissions: ['flow.access', 'flow.operations.execute'],
    tokenAliases: ['configurator-token'],
  },
  {
    code: 'flow-reviewer',
    displayName: 'FLOW Reviewer',
    subjectId: 'mock-flow-reviewer',
    username: 'flow-reviewer',
    email: 'flow-reviewer@oss-nwm.local',
    roles: ['flow-reviewer'],
    permissions: ['flow.access', 'flow.analytics.read'],
    tokenAliases: ['reviewer-token'],
  },
  {
    code: 'scanner-ack-operator',
    displayName: 'Scanner ACK Operator',
    subjectId: 'mock-scanner-ack-operator',
    username: 'scanner-ack-operator',
    email: 'scanner-ack-operator@oss-nwm.local',
    organizationId: 'org-1',
    roles: ['scanner-ack-operator'],
    // Deliberately narrow: holds ONLY the dedicated ACK-ingestion permission, not the broad
    // flow.operations.execute grant, to prove the manual ACK routes require this specific
    // permission rather than being satisfiable by generic operational access.
    permissions: ['flow.access', 'scanner.ack.ingest'],
    tokenAliases: ['scanner-ack-token'],
  },
] as const;

export function listMockAuthProfiles(): readonly MockAuthProfile[] {
  return MOCK_AUTH_PROFILES;
}

export function resolveMockAuthProfile(profileCode: string): MockAuthProfile {
  const profile = MOCK_AUTH_PROFILES.find((candidate) => candidate.code === profileCode);
  if (!profile) {
    throw new Error(`Unknown mock auth profile "${profileCode}"`);
  }
  return profile;
}

export function resolveMockAuthProfileByToken(
  token: string,
  fallbackProfileCode = 'platform-admin',
): MockAuthProfile {
  const profile = MOCK_AUTH_PROFILES.find((candidate) => candidate.tokenAliases?.includes(token));
  if (profile) {
    return profile;
  }
  return resolveMockAuthProfile(fallbackProfileCode);
}

export function buildMockAuthorizationSubject(profileCode: string): MockAuthorizationSubject {
  const profile = resolveMockAuthProfile(profileCode);
  return {
    subjectId: profile.subjectId,
    username: profile.username,
    displayName: profile.displayName,
    email: profile.email,
    organizationId: profile.organizationId,
    roles: [...profile.roles],
    permissions: [...profile.permissions],
    profileCode: profile.code,
  };
}

export function mockProfileHasAnyPermission(
  profileCode: string,
  permissions: readonly string[],
): boolean {
  const profile = resolveMockAuthProfile(profileCode);
  return hasAnyPermission(profile.permissions, permissions);
}

